import { useState, useCallback, useRef } from 'react';
import { applyResize, clampToPage } from '../utils/coordinateUtils';
import FIELD_TYPES from '../utils/fieldTypes';

/**
 * Hook for handling field placement, movement, and resizing interactions.
 */
const useFieldPlacement = ({
  pageDimensions,
  onAddField,
  onMoveField,
  onResizeField,
  onSelectField,
}) => {
  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [draggedFieldId, setDraggedFieldId] = useState(null);
  const [resizeHandle, setResizeHandle] = useState(null);

  // Click-to-place state (selected tool from palette)
  const [selectedToolType, setSelectedToolType] = useState(null);

  // Track start position for delta calculations
  const dragStartRef = useRef(null);
  const fieldStartRef = useRef(null);

  /**
   * Handle drag start from the field palette (creating new field)
   */
  const handlePaletteDragStart = useCallback((fieldType, event) => {
    event.dataTransfer.setData('application/field-type', fieldType);
    event.dataTransfer.effectAllowed = 'copy';

    // Create a custom drag image
    const dragImage = document.createElement('div');
    dragImage.textContent = FIELD_TYPES[fieldType]?.name || fieldType;
    dragImage.style.cssText = `
      padding: 8px 16px;
      background: ${FIELD_TYPES[fieldType]?.color || '#3B82F6'};
      color: white;
      border-radius: 4px;
      font-size: 14px;
      position: absolute;
      top: -1000px;
    `;
    document.body.appendChild(dragImage);
    event.dataTransfer.setDragImage(dragImage, 50, 20);

    // Clean up the drag image after a short delay
    setTimeout(() => {
      document.body.removeChild(dragImage);
    }, 0);
  }, []);

  /**
   * Handle drop on the PDF canvas (creates new field)
   */
  const handleCanvasDrop = useCallback((event, containerRect) => {
    event.preventDefault();

    const fieldType = event.dataTransfer.getData('application/field-type');
    if (!fieldType || !pageDimensions.width || !pageDimensions.height) {
      return;
    }

    // Calculate drop position in normalized coordinates
    const x = (event.clientX - containerRect.left) / pageDimensions.width;
    const y = (event.clientY - containerRect.top) / pageDimensions.height;

    // Adjust for field default size (center the field on cursor)
    const fieldTypeConfig = FIELD_TYPES[fieldType];
    const adjustedX = Math.max(0, x - fieldTypeConfig.defaultWidth / 2);
    const adjustedY = Math.max(0, y - fieldTypeConfig.defaultHeight / 2);

    const position = clampToPage({
      x: adjustedX,
      y: adjustedY,
      w: fieldTypeConfig.defaultWidth,
      h: fieldTypeConfig.defaultHeight,
    });

    onAddField(fieldType, { x: position.x, y: position.y });
  }, [pageDimensions, onAddField]);

  /**
   * Handle drag over the PDF canvas
   */
  const handleCanvasDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, []);

  /**
   * Handle mouse down on an existing field (start move or resize)
   */
  const handleFieldMouseDown = useCallback((fieldId, fieldArea, event, handle = null) => {
    event.stopPropagation();
    event.preventDefault();

    onSelectField(fieldId);

    // Store starting positions
    dragStartRef.current = {
      x: event.clientX,
      y: event.clientY,
    };
    fieldStartRef.current = { ...fieldArea };

    if (handle) {
      // Starting a resize operation
      setIsResizing(true);
      setResizeHandle(handle);
      setDraggedFieldId(fieldId);
    } else {
      // Starting a move operation
      setIsDragging(true);
      setDraggedFieldId(fieldId);
    }
  }, [onSelectField]);

  /**
   * Handle mouse move during drag/resize
   */
  const handleMouseMove = useCallback((event) => {
    if (!isDragging && !isResizing) return;
    if (!dragStartRef.current || !fieldStartRef.current) return;
    if (!pageDimensions.width || !pageDimensions.height) return;

    // Calculate delta in normalized coordinates
    const deltaX = (event.clientX - dragStartRef.current.x) / pageDimensions.width;
    const deltaY = (event.clientY - dragStartRef.current.y) / pageDimensions.height;

    if (isDragging && draggedFieldId) {
      // Moving the field
      const newPosition = clampToPage({
        x: fieldStartRef.current.x + deltaX,
        y: fieldStartRef.current.y + deltaY,
        w: fieldStartRef.current.w,
        h: fieldStartRef.current.h,
      });

      onMoveField(draggedFieldId, newPosition);
    } else if (isResizing && draggedFieldId && resizeHandle) {
      // Resizing the field
      // Get field type for min dimensions
      const newArea = applyResize(
        fieldStartRef.current,
        resizeHandle,
        { dx: deltaX, dy: deltaY },
        0.03, // minWidth
        0.02  // minHeight
      );

      onResizeField(draggedFieldId, newArea);
    }
  }, [isDragging, isResizing, draggedFieldId, resizeHandle, pageDimensions, onMoveField, onResizeField]);

  /**
   * Handle mouse up (end drag/resize)
   */
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setDraggedFieldId(null);
    setResizeHandle(null);
    dragStartRef.current = null;
    fieldStartRef.current = null;
  }, []);

  /**
   * Handle click on the canvas (place field if tool selected, otherwise deselect)
   */
  const handleCanvasClick = useCallback((event, containerRect) => {
    // Only handle if clicking directly on the canvas, not on a field
    if (!event.target.classList.contains('pdf-page-container') &&
        event.target.tagName !== 'CANVAS') {
      return;
    }

    // If a tool is selected, place a field at the click position
    if (selectedToolType && pageDimensions.width && pageDimensions.height) {
      const fieldTypeConfig = FIELD_TYPES[selectedToolType];
      if (!fieldTypeConfig || !containerRect) return;

      // Calculate click position in normalized coordinates
      const x = (event.clientX - containerRect.left) / pageDimensions.width;
      const y = (event.clientY - containerRect.top) / pageDimensions.height;

      // Center the field on cursor
      const adjustedX = Math.max(0, x - fieldTypeConfig.defaultWidth / 2);
      const adjustedY = Math.max(0, y - fieldTypeConfig.defaultHeight / 2);

      const position = clampToPage({
        x: adjustedX,
        y: adjustedY,
        w: fieldTypeConfig.defaultWidth,
        h: fieldTypeConfig.defaultHeight,
      });

      onAddField(selectedToolType, { x: position.x, y: position.y });

      // Clear selected tool after placing (user can click palette again for another)
      setSelectedToolType(null);
    } else {
      // No tool selected - deselect any selected field
      onSelectField(null);
    }
  }, [selectedToolType, pageDimensions, onAddField, onSelectField]);

  return {
    // State
    isDragging,
    isResizing,
    draggedFieldId,
    selectedToolType,

    // Tool selection (for click-to-place)
    setSelectedToolType,

    // Palette handlers
    handlePaletteDragStart,

    // Canvas handlers
    handleCanvasDrop,
    handleCanvasDragOver,
    handleCanvasClick,

    // Field handlers
    handleFieldMouseDown,

    // Global handlers (attach to window or container)
    handleMouseMove,
    handleMouseUp,
  };
};

export default useFieldPlacement;
